import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Globe,
  Plus,
  Trash2,
  ExternalLink,
  Clock,
  AlertCircle,
  FileCode,
  Eye,
  Loader2,
  ImagePlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import useDebounce from '@/client/hooks/useDebounce';
import type {
  LearnAction,
  LearnBlockGroup,
  LearnSurface,
} from '@/client/types/learn';
import {
  useAdminLearnMarkdownPreviewMutation,
  useAdminLearnSectionQuery,
  useAdminLearnTopicsQuery,
  useCreateLearnSectionMutation,
  usePublishLearnSectionMutation,
  useUnpublishLearnSectionMutation,
  useUpdateLearnSectionMutation,
} from '@/client/api/learn';
import { LearnBlockPreview } from './components/LearnBlockPreview';
import { InsertPhotoDialog } from './components/InsertPhotoDialog';
import { LegalReviewPanel } from './components/LegalReviewPanel';

function validateActionHref(href: string): { valid: boolean; error?: string } {
  const trimmed = href.trim();
  if (!trimmed) return { valid: false, error: 'URL cannot be blank' };
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return { valid: true };
  }
  if (trimmed.startsWith('https://')) {
    try {
      new URL(trimmed);
      return { valid: true };
    } catch {
      return { valid: false, error: 'Malformed https:// URL' };
    }
  }
  return {
    valid: false,
    error: 'Must be a relative path (e.g. /listing/post) or https:// URL',
  };
}

const DEFAULT_MARKDOWN_TEMPLATE = `## Overview {#overview}

Welcome to this guide. Explain the key concepts here clearly.

- Point one
- Point two

> [!tip]
> Provide a helpful tip or best practice for readers.

## Next Steps {#next-steps}

1. Follow step one
2. Follow step two

> [!warning]
> Highlight any common pitfalls or safety guidelines.
`;

const SectionEditorPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = Boolean(slug);
  const initialTopicFromQuery = searchParams.get('topic') || '';

  const { data: topics = [], isLoading: topicsLoading } = useAdminLearnTopicsQuery(true);
  const {
    data: existingSection,
    isLoading: sectionLoading,
    isError: sectionError,
  } = useAdminLearnSectionQuery(slug ?? null, isEdit);

  useBreadcrumbs([
    { label: 'Content', href: '/admin/content/pages' },
    { label: 'Learn', href: '/admin/content/learn' },
    ...(existingSection?.topic?.id
      ? [{ label: existingSection.topic.title, href: `/admin/content/learn/topics/${existingSection.topic.id}` }]
      : []),
    { label: isEdit ? (existingSection?.title || slug || 'Edit Section') : 'New Section' },
  ]);

  const createMutation = useCreateLearnSectionMutation();
  const updateMutation = useUpdateLearnSectionMutation();
  const publishMutation = usePublishLearnSectionMutation();
  const unpublishMutation = useUnpublishLearnSectionMutation();
  const previewMutation = useAdminLearnMarkdownPreviewMutation();

  // Form state
  const [topicId, setTopicId] = useState(initialTopicFromQuery);
  const [title, setTitle] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const [summary, setSummary] = useState('');
  const [minutes, setMinutes] = useState<number | ''>('');
  const [surface, setSurface] = useState<LearnSurface>('reference');
  const [published, setPublished] = useState(false);
  const [markdown, setMarkdown] = useState(DEFAULT_MARKDOWN_TEMPLATE);
  const [actions, setActions] = useState<LearnAction[]>([]);
  const [previewBlocks, setPreviewBlocks] = useState<LearnBlockGroup[]>([]);
  const [photoOpen, setPhotoOpen] = useState(false);
  const cursorRef = useRef<number | null>(null);

  // Track if we've initialized the form with existing data (to avoid clobbering user edits)
  const [hasLoadedExisting, setHasLoadedExisting] = useState(false);

  // Initialize form when section data is loaded
  useEffect(() => {
    if (existingSection && isEdit && !hasLoadedExisting) {
      setTopicId(existingSection.topic?.id || '');
      setTitle(existingSection.title || '');
      setCustomSlug(existingSection.slug || '');
      setSummary(existingSection.summary || '');
      setMinutes(existingSection.minutes ?? '');
      setSurface(existingSection.surface || 'reference');
      setPublished(existingSection.published ?? false);
      // If the backend has markdown source, use it; otherwise fall back to empty or default
      setMarkdown(existingSection.markdown || '');
      setActions(existingSection.actions || []);
      if (existingSection.blocks && existingSection.blocks.length > 0) {
        setPreviewBlocks(existingSection.blocks);
      }
      setHasLoadedExisting(true);
    } else if (!isEdit && initialTopicFromQuery && !topicId) {
      setTopicId(initialTopicFromQuery);
    }
  }, [existingSection, isEdit, hasLoadedExisting, initialTopicFromQuery, topicId]);

  // Debounced live markdown preview parsing
  const debouncedMarkdown = useDebounce(markdown, 300);

  useEffect(() => {
    if (!debouncedMarkdown.trim()) {
      setPreviewBlocks([]);
      return;
    }

    let isMounted = true;
    previewMutation.mutateAsync(debouncedMarkdown).then((blocks) => {
      if (isMounted && blocks) {
        setPreviewBlocks(blocks);
      }
    }).catch(() => {
      // Keep previous blocks on preview error
    });

    return () => {
      isMounted = false;
    };
  }, [debouncedMarkdown]);

  // Actions repeater handlers
  const handleAddAction = () => {
    setActions([...actions, { label: '', href: '' }]);
  };

  const handleUpdateAction = (index: number, field: 'label' | 'href', value: string) => {
    const next = [...actions];
    next[index] = { ...next[index], [field]: value };
    setActions(next);
  };

  const handleRemoveAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  // Helper insertion into Markdown textarea
  const handleInsertToken = (token: string) => {
    setMarkdown((prev) => `${prev}\n\n${token}\n`);
  };

  // Photos go where the cursor last was; at the end if the box was never clicked.
  const handleInsertAtCursor = (line: string) => {
    const caret = cursorRef.current;
    setMarkdown((prev) => {
      const at = caret === null ? prev.length : Math.min(caret, prev.length);
      return `${prev.slice(0, at).replace(/\s*$/, '')}\n\n${line}\n\n${prev.slice(at).replace(/^\s*/, '')}`;
    });
  };

  const handleSubmit = async (publishImmediate = false) => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!topicId) {
      toast.error('Topic is required');
      return;
    }

    // Validate actions
    for (let i = 0; i < actions.length; i++) {
      const act = actions[i];
      if (!act.label.trim()) {
        toast.error(`Action #${i + 1} label cannot be empty`);
        return;
      }
      const val = validateActionHref(act.href);
      if (!val.valid) {
        toast.error(`Action #${i + 1} invalid URL: ${val.error}`);
        return;
      }
    }

    const finalPublished = publishImmediate || published;

    try {
      if (isEdit && slug) {
        const payload = {
          slug,
          topic: topicId,
          title: title.trim(),
          new_slug: customSlug.trim() !== slug ? customSlug.trim() : undefined,
          summary: summary.trim() || undefined,
          markdown,
          actions: actions.length > 0 ? actions : undefined,
          minutes: minutes === '' ? undefined : Number(minutes),
          published: finalPublished,
          surface,
        };
        const res = await updateMutation.mutateAsync(payload);
        toast.success('Section updated successfully');
        if (res.slug && res.slug !== slug) {
          navigate(`/admin/content/learn/sections/${encodeURIComponent(res.slug)}`, { replace: true });
        }
      } else {
        const payload = {
          topic: topicId,
          title: title.trim(),
          slug: customSlug.trim() || undefined,
          summary: summary.trim() || undefined,
          markdown,
          actions: actions.length > 0 ? actions : undefined,
          minutes: minutes === '' ? undefined : Number(minutes),
          published: finalPublished,
          surface,
        };
        const res = await createMutation.mutateAsync(payload);
        toast.success('Section created successfully');
        navigate(`/admin/content/learn/sections/${encodeURIComponent(res.slug)}`, { replace: true });
      }
    } catch (err: any) {
      toast.error(err?.message || (isEdit ? 'Failed to update section' : 'Failed to create section'));
    }
  };

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    publishMutation.isPending ||
    unpublishMutation.isPending;

  if (isEdit && sectionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-44 w-full rounded-xl" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-96 rounded-xl" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  if (isEdit && (sectionError || !existingSection)) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-2xl rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
          <h2 className="mt-3 text-lg font-bold text-gray-900">Section Not Found</h2>
          <p className="mt-1 text-sm text-gray-500">
            The section with slug &quot;{slug}&quot; could not be loaded.
          </p>
          <Button asChild className="mt-4 gap-2">
            <Link to="/admin/content/learn">
              <ArrowLeft className="h-4 w-4" /> Return to Learn
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const selectedTopic = topics.find((t) => t.id === topicId);
  const legal = isEdit ? existingSection?.legal : undefined;
  const publishBlocked = Boolean(legal?.blocks_publish) && !published;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Top Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild className="text-gray-600 hover:text-gray-900">
              <Link to={topicId ? `/admin/content/learn/topics/${encodeURIComponent(topicId)}` : '/admin/content/learn'}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                {selectedTopic ? `Back to ${selectedTopic.title}` : 'Back'}
              </Link>
            </Button>
            <div className="h-4 w-px bg-gray-300" />
            <h1 className="text-xl font-bold text-gray-900">
              {isEdit ? `Edit: ${existingSection?.title || slug}` : 'New Learn Section'}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {isEdit && (
              <Button variant="outline" size="sm" asChild className="gap-1.5 text-gray-600">
                <a href={`/learn/${slug}`} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                  View Public
                </a>
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSubmit(false)}
              disabled={isPending}
              className="gap-1.5"
            >
              <Save className="h-4 w-4 text-gray-500" />
              {published ? 'Save Changes' : 'Save as Draft'}
            </Button>

            <Button
              size="sm"
              onClick={() => handleSubmit(!published)}
              disabled={isPending || publishBlocked}
              title={publishBlocked ? 'Clear the legal review below before publishing' : undefined}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Globe className="h-4 w-4" />
              {published ? 'Update & Keep Live' : 'Publish Section'}
            </Button>
          </div>
        </div>

        {/* Metadata Configuration Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
            Section Settings & Metadata
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Topic Selector */}
            <div className="space-y-1.5">
              <Label htmlFor="sec-topic">Parent Topic *</Label>
              <select
                id="sec-topic"
                value={topicId}
                onChange={(e) => setTopicId(e.target.value)}
                disabled={topicsLoading}
                className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:opacity-50"
              >
                <option value="" disabled>Select topic</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title} ({t.id})
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="sec-title">Section Title *</Label>
              <Input
                id="sec-title"
                value={title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                placeholder="e.g. Post your first listing"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Slug */}
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="sec-slug">Slug / URL Path</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-gray-400">/learn/</span>
                <Input
                  id="sec-slug"
                  value={customSlug}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomSlug(e.target.value)}
                  placeholder="auto-derived-from-title"
                />
              </div>
              {isEdit && existingSection?.canonical_slug && (
                <p className="text-[11px] text-gray-500">
                  Canonical URL: <span className="font-mono">/learn/{existingSection.canonical_slug}</span>. Changing slug creates an automatic redirect history.
                </p>
              )}
            </div>

            {/* Reading Minutes */}
            <div className="space-y-1.5">
              <Label htmlFor="sec-minutes">Est. Read Time (minutes)</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <Input
                  id="sec-minutes"
                  type="number"
                  min="1"
                  max="120"
                  value={minutes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinutes(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Auto (e.g. 5)"
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Summary */}
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="sec-summary">Summary (Card / Meta Description)</Label>
              <Input
                id="sec-summary"
                value={summary}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSummary(e.target.value)}
                placeholder="Brief takeaway explaining what the reader will accomplish in this section..."
              />
            </div>

            {/* Surface Type & Publish Switch */}
            <div className="flex items-center gap-4 justify-between border border-gray-100 rounded-lg p-3 bg-gray-50/50">
              <div className="space-y-0.5">
                <Label className="text-xs font-semibold">Published</Label>
                <p className="text-[11px] text-gray-500">
                  {published ? 'Live on /learn' : 'Draft only'}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Call-to-Action Buttons Repeater */}
          <div className="pt-2 border-t border-gray-100 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-semibold">Call-to-Action Buttons (Optional)</Label>
                <p className="text-[11px] text-gray-500">
                  Bottom CTA buttons pointing readers to next steps (e.g. &quot;Post a listing&quot; &rarr; <code>/listing/post</code>).
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={handleAddAction} className="gap-1 text-xs">
                <Plus className="h-3.5 w-3.5" /> Add CTA
              </Button>
            </div>

            {actions.length > 0 && (
              <div className="space-y-2">
                {actions.map((act, index) => {
                  const check = validateActionHref(act.href);
                  return (
                    <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 rounded-lg border border-gray-200 p-2.5 bg-gray-50/60">
                      <div className="flex-1 w-full sm:w-auto">
                        <Input
                          placeholder="Button Label (e.g. Post a listing)"
                          value={act.label}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateAction(index, 'label', e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="flex-1 w-full sm:w-auto">
                        <Input
                          placeholder="Relative path (/path) or https:// URL"
                          value={act.href}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateAction(index, 'href', e.target.value)}
                          className={`h-8 text-xs font-mono ${act.href && !check.valid ? 'border-red-400 bg-red-50/50' : ''}`}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveAction(index)}
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {isEdit && slug && legal?.required && <LegalReviewPanel slug={slug} legal={legal} />}

        {/* Two-Pane Editor: Markdown Source vs Live Block Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Left Pane: Markdown Source Authoring */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <FileCode className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-900">Markdown Source</span>
              </div>
              <span className="text-xs text-gray-400 font-mono">
                {markdown.length} chars
              </span>
            </div>

            {/* Quick Token Insertion Toolbar */}
            <div className="flex items-center gap-1.5 flex-wrap pb-1">
              <span className="text-[11px] text-gray-400 mr-1 font-medium">Insert:</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleInsertToken('## Section Heading {#id}')}
                className="h-7 text-xs px-2"
              >
                Heading
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleInsertToken('> [!tip]\n> Pro-tip guidance here.')}
                className="h-7 text-xs px-2 text-emerald-700 bg-emerald-50/50 border-emerald-200"
              >
                [!tip]
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleInsertToken('> [!warning]\n> Important caution or requirement.')}
                className="h-7 text-xs px-2 text-amber-700 bg-amber-50/50 border-amber-200"
              >
                [!warning]
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleInsertToken('> [!note]\n> Additional context or detail.')}
                className="h-7 text-xs px-2 text-blue-700 bg-blue-50/50 border-blue-200"
              >
                [!note]
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleInsertToken('- First bullet\n- Second bullet')}
                className="h-7 text-xs px-2"
              >
                Bullet list
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleInsertToken('1. Step one\n2. Step two')}
                className="h-7 text-xs px-2"
              >
                Numbered list
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPhotoOpen(true)}
                disabled={!isEdit}
                title={isEdit ? 'Upload a photo and insert it at the cursor' : 'Save the section as a draft first'}
                className="h-7 text-xs px-2 gap-1"
              >
                <ImagePlus className="h-3.5 w-3.5" /> Photo
              </Button>
            </div>

            <textarea
              onSelect={(e: React.SyntheticEvent<HTMLTextAreaElement>) => {
                cursorRef.current = e.currentTarget.selectionStart;
              }}
              value={markdown}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMarkdown(e.target.value)}
              placeholder="Write section content in markdown..."
              className="w-full min-h-[500px] font-mono text-xs leading-relaxed resize-y rounded-md border border-gray-300 p-3.5 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />

            <div className="text-[11px] text-gray-400 flex items-center justify-between">
              <span>
                Callouts: <code>&gt; [!tip]</code>, <code>&gt; [!warning]</code>, <code>&gt; [!note]</code>. Photos:{' '}
                <code>![description](url &quot;caption&quot;)</code>. Deleting a photo line removes the photo.
              </span>
              {previewMutation.isPending && (
                <span className="flex items-center gap-1 text-blue-600">
                  <Loader2 className="h-3 w-3 animate-spin" /> Parsing...
                </span>
              )}
            </div>
          </div>

          {/* Right Pane: Live Block Preview */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-semibold text-gray-900">Live Block Preview</span>
              </div>
              <Badge variant="secondary" className="text-[10px] text-gray-500">
                Rendered Blocks ({previewBlocks.reduce((acc, g) => acc + (g.blocks?.length || 0), 0)})
              </Badge>
            </div>

            <LearnBlockPreview
              title={title || 'Section Title'}
              summary={summary}
              minutes={minutes === '' ? undefined : Number(minutes)}
              topicTitle={selectedTopic?.title}
              blocks={previewBlocks}
              actions={actions.filter((a) => a.label && a.href)}
              className="min-h-[500px]"
            />
          </div>
        </div>
      </div>

      {isEdit && slug && (
        <InsertPhotoDialog
          slug={slug}
          open={photoOpen}
          onOpenChange={setPhotoOpen}
          onInsert={handleInsertAtCursor}
        />
      )}
    </div>
  );
};

export default SectionEditorPage;
